import { returnProgramCover, finishProgramCover, cancelProgramCover, revealProgramQuestions } from '@/utils/programCoverMotion';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import api, { fileUrl } from '@/api';
import { toast } from 'sonner';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { RichText, stripHtml } from '@/components/RichTextEditor';
import { reachableQuestionIds, isAnswerEmpty } from '@/utils/programForm';
import { Buildings, CheckCircle, Globe, Lock, Sparkle, ChartLineUp, Trophy, ArrowRight, ArrowLeft, CaretLeft, FileText, Lightning, ShieldCheck, FloppyDisk, Clock } from '@phosphor-icons/react';
import PhoneField, { isValidPhoneNumber } from '@/components/PhoneField';
import { SectionedForm, isUploadType, ProgramPublicFrame, ProgramApplyPrompt } from '@/components/ProgramApplySplit';

// Quick-Apply drafts are resumed with a temporary id, not a login. It arrives either in
// the emailed resume link (?draft=…) or from this browser's own last save.
const qaDraftKey = (slug) => `qa_draft_token_${slug}`;
const readQuickDraftToken = (slug, search) => {
  try {
    return new URLSearchParams(search || '').get('draft') || localStorage.getItem(qaDraftKey(slug)) || '';
  } catch { return ''; }
};

const PublicApply = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // A resume token means they already chose Quick Apply on an earlier visit — drop them
  // straight back into that form instead of re-asking how they'd like to apply.
  const initialDraftToken = readQuickDraftToken(slug, location.search);

  // Apply-choice popup (unauthenticated startups): pick "Founderboard" vs "Quick Apply".
  const [showChoiceModal, setShowChoiceModal] = useState(false);
  const [pickedQuick, setPickedQuick] = useState(!!initialDraftToken);
  const [accountCreated, setAccountCreated] = useState(false);  // backend auto-created an account on quick apply
  const [recommendedPrograms, setRecommendedPrograms] = useState([]);
  const [recommendationsLoading, setRecommendationsLoading] = useState(false);

  // Form state
  const [applicantEmail, setApplicantEmail] = useState('');
  const [applicantName, setApplicantName] = useState('');
  const [applicantPhone, setApplicantPhone] = useState('');
  const [answers, setAnswers] = useState({});
  const [fileUploads, setFileUploads] = useState({}); // {qid: {file_id, filename}}
  const [uploadingQid, setUploadingQid] = useState(null);

  // Save-as-draft — logged-in founders keep a draft against their account; Quick Apply
  // keeps one against a temporary id (`quickDraftToken`).
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);
  const [quickDraftToken, setQuickDraftToken] = useState(initialDraftToken);
  const [quickDraftSavedAt, setQuickDraftSavedAt] = useState(null);
  // Only set when a draft was rehydrated from the server — saving mid-form must NOT
  // re-run the jump-to-where-you-left-off navigation and move the page under them.
  const [quickDraftResumed, setQuickDraftResumed] = useState(false);
  const [draftStatus, setDraftStatus] = useState(null);   // {exists, already_applied, updated_at, answered_count}
  const [sendingResumeLink, setSendingResumeLink] = useState(false);

  // Auth state
  const storedUser = (() => { try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; } })();
  const isLoggedIn = !!storedUser && storedUser.role === 'startup';
  const applicationSource = new URLSearchParams(location.search || '').get('source');
  const isNewProgramSignupFlow = isLoggedIn && sessionStorage.getItem('new_startup_program_flow_slug') === slug;
  const showApplicationBackButton = (!isLoggedIn && pickedQuick) || (isLoggedIn && applicationSource === 'programs' && !isNewProgramSignupFlow);
  const applicationBackLabel = !isLoggedIn && pickedQuick
    ? 'Back to application options'
    : 'Back to Programs';

  const handleApplicationBack = () => {
    if (!isLoggedIn && pickedQuick) {
      // Quick Apply stays on this public program page, so "back" means returning
      // to the choice between Basic Apply and FounderBoard Profile.
      setPickedQuick(false);
      setShowChoiceModal(true);
      return;
    }

    if (isLoggedIn && applicationSource === 'programs') {
      returnProgramCover(slug);
      navigate('/startup/programs');
    }
  };
  useLayoutEffect(() => {
    if (error) cancelProgramCover();
    else if (!loading && program && !submitted) finishProgramCover('open', slug);
  }, [loading, program, error, submitted, slug]);
  useEffect(() => {
    if (applicationSource !== 'programs') return;
    const onBack = () => returnProgramCover(slug);
    const onContinue = event => { if (event.target.closest('[data-testid="mobile-continue-to-form-btn"]')) revealProgramQuestions(); };
    window.addEventListener('popstate', onBack);
    document.addEventListener('click', onContinue);
    return () => { window.removeEventListener('popstate', onBack); document.removeEventListener('click', onContinue); };
  }, [applicationSource, slug]);
  const [myStartup, setMyStartup] = useState(null);
  const [emailCheck, setEmailCheck] = useState(null); // {exists, role, has_profile}
  const [emailCheckLoading, setEmailCheckLoading] = useState(false);

  useEffect(() => {
    const fetchProgram = async () => {
      try {
        const res = await api.get(`/programs/public/${slug}`);
        setProgram(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Program not found');
      } finally { setLoading(false); }
    };
    fetchProgram();
  }, [slug]);

  // Once Quick Apply is submitted, surface other programs that are live right now.
  // This endpoint is intentionally public so the recommendations are useful before login.
  useEffect(() => {
    if (!submitted) return;
    let cancelled = false;
    const fetchRecommendations = async () => {
      setRecommendationsLoading(true);
      try {
        const res = await api.get(`/programs/public/${slug}/recommendations`, { params: { limit: 3 } });
        if (!cancelled) setRecommendedPrograms(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Failed to load live program recommendations', err?.response?.status, err?.response?.data || err);
        if (!cancelled) setRecommendedPrograms([]);
      } finally {
        if (!cancelled) setRecommendationsLoading(false);
      }
    };
    fetchRecommendations();
    return () => { cancelled = true; };
  }, [submitted, slug]);

  // Auto-open the apply-choice popup once for an unauthenticated visitor on an open program.
  useEffect(() => {
    if (program && program.status === 'open' && !isLoggedIn && !pickedQuick) {
      setShowChoiceModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [program, isLoggedIn]);

  // If logged in as startup, fetch their profile preview
  useEffect(() => {
    if (!isLoggedIn) return;
    const fetchMine = async () => {
      try {
        const res = await api.get('/startup/dashboard');
        setMyStartup(res.data?.startup);
        if (storedUser?.email) setApplicantEmail(storedUser.email);
        if (storedUser?.full_name) setApplicantName(storedUser.full_name);
      } catch { /* swallow */ }
    };
    fetchMine();
  }, [isLoggedIn]);

  // Debounced email check on the bare-form path. Same pass asks whether this email
  // already has a Quick-Apply draft for this program — that's the "come back and resume
  // by typing your email" entry point.
  useEffect(() => {
    if (isLoggedIn) return;
    const e = applicantEmail.trim();
    if (!e || !e.includes('@') || !e.includes('.')) { setEmailCheck(null); setDraftStatus(null); return; }
    setEmailCheckLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.get(`/auth/check-email/${encodeURIComponent(e)}`);
        setEmailCheck(res.data);
      } catch { setEmailCheck(null); }
      finally { setEmailCheckLoading(false); }
      if (quickDraftToken) { setDraftStatus(null); return; }   // already resuming — nothing to nudge about
      try {
        const ds = await api.get(`/programs/public/${slug}/draft-status`, { params: { email: e } });
        setDraftStatus(ds.data);
      } catch { setDraftStatus(null); }
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantEmail, isLoggedIn, quickDraftToken, slug]);

  // Rehydrate a Quick-Apply draft from its temporary id (emailed resume link, or this
  // browser's last save). Answers, choices and uploaded files all come back.
  useEffect(() => {
    if (isLoggedIn || !quickDraftToken || !program?.id || program.status !== 'open' || quickDraftResumed) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/programs/public/${slug}/draft`, { params: { token: quickDraftToken } });
        const d = res.data;
        if (cancelled || !d) return;
        if (d.applicant_email) setApplicantEmail(d.applicant_email);
        if (d.applicant_name) setApplicantName(d.applicant_name);
        if (d.applicant_phone) setApplicantPhone(d.applicant_phone);
        if (d.answers && Object.keys(d.answers).length) setAnswers(d.answers);
        if (d.file_uploads && Object.keys(d.file_uploads).length) setFileUploads(d.file_uploads);
        setQuickDraftSavedAt(d.updated_at || null);
        setQuickDraftResumed(true);
        setPickedQuick(true);
        localStorage.setItem(qaDraftKey(slug), quickDraftToken);
        toast.success('Draft restored — pick up where you left off.');
      } catch (err) {
        if (cancelled) return;
        // Expired, discarded, or already submitted — forget the token and start clean.
        localStorage.removeItem(qaDraftKey(slug));
        setQuickDraftToken('');
        toast.error(err.response?.data?.detail || 'That draft link is no longer valid — starting a fresh application.');
        setShowChoiceModal(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, quickDraftToken, program?.id]);

  const uploadQuestionFile = async (qid, file, isAuthed) => {
    if (!file) return;
    setUploadingQid(qid);
    try {
      const fd = new FormData();
      fd.append('file', file);
      let res;
      if (isAuthed) {
        res = await api.post('/files/upload', fd);
      } else {
        res = await api.post(`/files/upload-public?slug=${encodeURIComponent(slug)}`, fd);
      }
      setFileUploads({ ...fileUploads, [qid]: { file_id: res.data.file_id, filename: res.data.filename } });
      toast.success(`${res.data.filename} uploaded`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploadingQid(null); }
  };

  const submitAnonymous = async (e) => {
    e?.preventDefault();
    if (!applicantName.trim()) { toast.error('Name required'); return; }
    if (!applicantEmail.trim()) { toast.error('Email required'); return; }
    if (!applicantPhone.trim()) { toast.error('Phone number required'); return; }
    if (!isValidPhoneNumber(applicantPhone)) { toast.error('Please enter a valid phone number'); return; }
    // Only validate required questions on the branch path the applicant actually reached.
    const reachable = reachableQuestionIds(program.questions || [], answers);
    for (const q of program.questions || []) {
      if (!q.required || !reachable.has(q.id)) continue;
      if (isUploadType(q.type)) {
        if (!fileUploads[q.id]?.file_id) { toast.error(`Please upload: ${stripHtml(q.label)}`); return; }
      } else if (isAnswerEmpty(answers[q.id])) {
        toast.error(`Please answer: ${stripHtml(q.label)}`); return;
      }
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/programs/public/${slug}/apply`, {
        applicant_email: applicantEmail.trim(),
        applicant_name: applicantName.trim(),
        applicant_phone: applicantPhone.trim(),
        answers,
        file_uploads: Object.fromEntries(Object.entries(fileUploads).map(([k, v]) => [k, v.file_id])),
        draft_token: quickDraftToken || undefined,
      });
      setAccountCreated(!!res.data?.account_created);
      // Submitted — the draft (and its resume link) is retired server-side.
      localStorage.removeItem(qaDraftKey(slug));
      setQuickDraftToken('');
      setSubmitted(true);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit'); }
    finally { setSubmitting(false); }
  };

  const submitAuthed = async () => {
    const authedPhone = applicantPhone.trim() || storedUser.phone || '';
    if (!authedPhone) { toast.error('Phone number required'); return; }
    if (!isValidPhoneNumber(authedPhone)) { toast.error('Please enter a valid phone number'); return; }
    const reachable = reachableQuestionIds(program.questions || [], answers);
    for (const q of program.questions || []) {
      if (!q.required || !reachable.has(q.id)) continue;
      if (isUploadType(q.type)) {
        if (!fileUploads[q.id]?.file_id) { toast.error(`Please upload: ${stripHtml(q.label)}`); return; }
      } else if (isAnswerEmpty(answers[q.id])) {
        toast.error(`Please answer: ${stripHtml(q.label)}`); return;
      }
    }
    setSubmitting(true);
    try {
      await api.post(`/programs/${program.id}/apply-authed`, {
        applicant_email: storedUser.email,
        applicant_name: storedUser.full_name || '',
        applicant_phone: authedPhone,
        answers,
        file_uploads: Object.fromEntries(Object.entries(fileUploads).map(([k, v]) => [k, v.file_id])),
      });
      // New founders who arrived through a public program link should continue
      // straight into Pre Dashboard after submitting — no intermediate confirmation screen.
      if (isNewProgramSignupFlow) {
        sessionStorage.removeItem('new_startup_program_flow_slug');
        sessionStorage.setItem('pre_dashboard_destination', '/startup/dashboard');
        if (program?.fund_name) {
          sessionStorage.setItem('pre_dashboard_incubator_name', program.fund_name);
        } else {
          sessionStorage.removeItem('pre_dashboard_incubator_name');
        }


        navigate('/pre-dashboard', { replace: true });
        return;
      }

      // Existing authenticated founders keep the standard submission confirmation.
      navigate(`/apply/${encodeURIComponent(slug)}/confirmation`, { replace: true });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit'); }
    finally { setSubmitting(false); }
  };

  // Restore a previously saved draft so logged-in founders pick up where they left off.
  useEffect(() => {
    if (!isLoggedIn || !program?.id || program.status !== 'open') return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/programs/${program.id}/draft`);
        const d = res.data;
        if (cancelled || !d) return;
        if (d.answers && Object.keys(d.answers).length) setAnswers(d.answers);
        if (d.file_uploads && Object.keys(d.file_uploads).length) setFileUploads(d.file_uploads);
        if (d.applicant_phone) setApplicantPhone(d.applicant_phone);
        setDraftRestored(true);
        toast.success('Draft restored — pick up where you left off.');
      } catch { /* no draft / not logged in — start fresh */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, program?.id]);

  // Persist the in-progress form as a draft the founder can resume later.
  const saveDraft = async () => {
    if (!isLoggedIn || !program?.id) return;
    setSavingDraft(true);
    try {
      await api.put(`/programs/${program.id}/draft`, {
        answers,
        file_uploads: fileUploads,
        applicant_phone: applicantPhone.trim(),
      });
      setDraftRestored(true);
      toast.success('Draft saved — resume anytime from “My Applications”.');
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not save draft'); }
    finally { setSavingDraft(false); }
  };

  // Quick Apply save-as-draft — no account needed. The backend mints a temporary id we
  // keep in this browser, and emails a resume link that works from anywhere.
  const saveQuickDraft = async () => {
    const email = applicantEmail.trim();
    if (!email || !email.includes('@')) { toast.error('Add your email first — it’s how we find your draft later.'); return; }
    setSavingDraft(true);
    try {
      const res = await api.post(`/programs/public/${slug}/draft`, {
        draft_token: quickDraftToken || undefined,
        applicant_email: email,
        applicant_name: applicantName.trim(),
        applicant_phone: applicantPhone.trim(),
        answers,
        file_uploads: fileUploads,
      });
      const token = res.data?.draft_token;
      if (token) {
        setQuickDraftToken(token);
        localStorage.setItem(qaDraftKey(slug), token);
      }
      setQuickDraftSavedAt(res.data?.updated_at || new Date().toISOString());
      setDraftStatus(null);
      toast.success(res.data?.resume_link_emailed
        ? `Draft saved — we’ve emailed ${email} a link to resume from any device.`
        : 'Draft saved. Resume anytime from this browser or your emailed link.');
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not save draft'); }
    finally { setSavingDraft(false); }
  };

  // Coming back on a different device: mail the resume link for whatever draft this
  // email has. Deliberately says the same thing whether or not a draft exists.
  const sendResumeLink = async () => {
    const email = applicantEmail.trim();
    if (!email) return;
    setSendingResumeLink(true);
    try {
      const res = await api.post(`/programs/public/${slug}/draft/resume-link`, { applicant_email: email });
      toast.success(res.data?.message || 'Resume link sent — check your inbox.');
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not send the resume link'); }
    finally { setSendingResumeLink(false); }
  };

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center text-text-muted">Loading...</div>;
  if (error) return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="bg-surface border border-border rounded-xl p-8 max-w-md text-center" data-testid="apply-error">
        <Buildings size={48} className="text-text-muted mx-auto mb-4" weight="thin" />
        <h2 className="text-xl font-heading font-bold text-text-primary mb-2">Program not found</h2>
        <p className="text-sm text-text-secondary mb-5">{error}</p>
        <button onClick={() => navigate('/')} className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90">Go to Founderboard</button>
      </div>
    </div>
  );

  const isIncubator = program.entity_type === 'incubator';
  // ============= POST-SUBMIT EXPERIENCE =============
  // Keep the applicant visually inside the program they just completed: the left
  // side reuses the incubator's own cover photo, while the right side transitions
  // into FounderBoard and owns the primary CTA.
  if (submitted) {
    const successCover = program.cover_images?.[0];

    return (
      <div className="min-h-[100dvh] w-full overflow-x-hidden bg-white text-[#111118] lg:h-[100dvh] lg:overflow-hidden" data-testid="apply-success">
        <div className="grid min-h-[100dvh] w-full lg:h-full lg:grid-cols-[46%_54%]">
          {/* Same program imagery the applicant saw while filling the form. */}
          <section className="relative isolate flex min-h-[42dvh] overflow-hidden bg-slate-950 text-white lg:h-full" data-testid="success-program-panel">
            {successCover ? (
              <>
                <img
                  src={fileUrl(successCover)}
                  alt=""
                  className="absolute inset-0 h-full w-full object-cover"
                  data-testid="success-cover-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/45 to-black/15" />
                <div className="absolute inset-0 bg-gradient-to-r from-black/20 via-transparent to-black/10" />
              </>
            ) : (
              <div className={`absolute inset-0 bg-gradient-to-br ${isIncubator ? 'from-warning/90 via-warning/60 to-slate-950' : 'from-primary/90 via-primary/60 to-slate-950'}`}>
                <div className="absolute -left-20 -top-20 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
                <div className="absolute -bottom-24 right-0 h-80 w-80 rounded-full bg-black/25 blur-3xl" />
              </div>
            )}

            <div className="relative z-10 flex min-h-full w-full flex-col px-6 py-6 sm:px-9 sm:py-8 lg:px-10 lg:py-10 xl:px-14 xl:py-12">
              <div className="mx-auto flex w-full max-w-[610px] items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                {program.fund_logo_url ? (
                  <img src={fileUrl(program.fund_logo_url)} className="h-11 w-11 rounded-lg bg-white/10 object-cover" alt={program.fund_name} />
                ) : (
                  <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-white/15">
                    <Buildings size={22} weight="bold" />
                  </div>
                )}
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-white/60">{isIncubator ? 'Incubator Program' : 'Funding Watchlist'}</div>
                  <div className="truncate font-heading text-sm font-bold text-white">{program.fund_name || 'FounderBoard'}</div>
                </div>
                </div>
                <button
                  type="button"
                  onClick={() => document.getElementById('founderboard-next')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  className="flex shrink-0 items-center gap-1.5 rounded-full border border-white/20 bg-black/20 px-2.5 py-1.5 text-[9px] font-bold uppercase tracking-[0.12em] text-white/80 backdrop-blur-sm lg:hidden"
                  aria-label="Scroll to FounderBoard"
                >
                  More below
                  <ArrowRight size={12} weight="bold" className="rotate-90 animate-bounce" />
                </button>
              </div>

              <div className="mx-auto flex w-full max-w-[610px] flex-1 flex-col justify-center py-12 sm:py-16 lg:py-14 xl:py-16">
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-300/25 bg-emerald-300/15 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-emerald-200 backdrop-blur-md sm:text-[11px]">
                  <CheckCircle size={15} weight="fill" /> Application sent
                </div>

                <h1 className="mt-5 max-w-[610px] font-heading text-[42px] font-extrabold leading-[0.98] tracking-[-0.045em] sm:text-[50px] lg:text-[54px] xl:text-[62px] 2xl:text-[66px]">
                  <span className="block text-white">You&apos;re in.</span>
                  <span className="mt-2 block text-white sm:mt-3">
                    Now keep the<br className="hidden sm:block" /> momentum.
                  </span>
                </h1>
                <p className="mt-5 max-w-[520px] text-[13px] leading-6 text-white/72 sm:text-[14px] sm:leading-6">
                  Your application to <strong className="font-semibold text-white">{program.fund_name}</strong> is in. You&apos;ve already done the hard part — now keep your startup moving while the program reviews it.
                </p>

                <div className="mt-8 max-w-[520px] border-l border-white/25 pl-4 sm:mt-10">
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-white/50">Application</p>
                  <p className="mt-1 text-[13px] font-semibold text-white sm:text-sm">{program.name}</p>
                  <p className="mt-1 text-[11px] leading-5 text-white/55">Updates will be sent to <span className="break-all text-white/80">{applicantEmail}</span>.</p>
                </div>

                <div className="mt-9 max-w-[520px] border-t border-white/15 pt-5 sm:mt-11" data-testid="success-next-steps">
                  <p className="text-[11px] leading-5 text-white/55 sm:text-[12px]">
                    Your FounderBoard account keeps this application, your startup profile and future opportunities together — ready when you are.
                  </p>
                </div>
              </div>

              <div className="mx-auto flex w-full max-w-[610px] items-center justify-center gap-2 pt-6 lg:pt-8" data-testid="success-powered-by">
                <img src="/founderboard-logo.png" alt="" className="h-8 w-8 rounded object-contain" />
                <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-white/85">Powered by FounderBoard</span>
              </div>
            </div>
          </section>

          {/* FounderBoard hand-off. The CTA now lives here, where the product is explained. */}
          <main id="founderboard-next" className="relative flex min-h-[58dvh] flex-col overflow-hidden bg-white px-6 py-7 sm:px-9 sm:py-9 lg:h-full lg:overflow-hidden lg:px-12 lg:py-5 xl:px-16 xl:py-6 2xl:px-20">
            <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-violet-100/75 blur-3xl" />
            <div className="pointer-events-none absolute bottom-[-130px] left-[-80px] h-72 w-72 rounded-full bg-emerald-50 blur-3xl" />

            <button type="button" onClick={() => navigate('/')} className="relative z-10 inline-flex w-fit items-center gap-3" aria-label="FounderBoard home">
              <img src="/founderboard-logo.png" alt="FounderBoard" className="h-9 w-9 rounded-[9px] object-contain" />
              <span className="font-heading text-[15px] font-bold tracking-[-0.2px] text-[#111118]">FounderBoard</span>
            </button>

            <div className="relative z-10 my-auto w-full max-w-[680px] py-9 sm:py-11 lg:py-0 xl:py-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.17em] text-violet-600 sm:text-[11px]">What your account gives you</p>
              <h2 className="mt-3 max-w-[620px] font-heading text-[30px] font-bold leading-[1.06] tracking-[-0.04em] text-[#111118] sm:text-[36px] lg:text-[26px] xl:text-[29px] lg:leading-[1.1]">
                FounderBoard is where the rest of it happens.
              </h2>

              <div className="mt-5 grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:mt-3 lg:gap-y-2" data-testid="founderboard-benefits">
                {[
                  ['Track Application Status', 'See where this application stands, without emailing to ask.'],
                  ['Manage Your Business Better', 'Keep your company profile, team and milestones current in one place.'],
                  ['Get More Incubator Programs', 'Apply to other programs without filling the same information again.'],
                  ['Get Credits for Your Business', 'Access credits and offers available to onboarded startups.'],
                  ['Keep Documents in One Place', 'Store your pitch deck, incorporation papers and financials for reuse.'],
                  ['Stay Investor-Ready', 'Keep updates, milestones and key startup information ready to share when opportunities appear.'],
                ].map(([title, copy]) => (
                  <div key={title} className="flex items-start gap-3">
                    <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-[3px] bg-emerald-500 shadow-[0_0_0_4px_rgba(16,185,129,0.08)]" />
                    <div>
                      <p className="text-[12px] font-bold leading-5 text-[#16161d] sm:text-[13px]">{title}</p>
                      <p className="mt-0.5 text-[10px] leading-[1.55] text-slate-500 sm:text-[11px]">{copy}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-5 flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/70 px-4 py-3 lg:mt-3 lg:py-2" data-testid="founderboard-social-proof">
                <div className="flex -space-x-1.5" aria-hidden="true">
                  <span className="h-6 w-6 rounded-full border-2 border-white bg-[#0d0b18]" />
                  <span className="h-6 w-6 rounded-full border-2 border-white bg-violet-500" />
                  <span className="h-6 w-6 rounded-full border-2 border-white bg-emerald-400" />
                </div>
                <p className="text-[10px] leading-4 text-slate-500 sm:text-[11px]">
                  <strong className="font-bold text-[#111118]">180+ startups</strong> have already onboarded to FounderBoard.
                </p>
              </div>

              <div className="mt-6 border-t border-slate-100 pt-5 sm:mt-7 lg:mt-3 lg:pt-3" data-testid="other-live-programs">
                <div className="mb-3 flex items-end justify-between gap-3 lg:mb-2">
                  <div>
                    <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-slate-400">Live now on FounderBoard</p>
                    <h3 className="mt-1 font-heading text-[15px] font-bold text-[#111118] sm:text-[16px]">Apply to other Incubators and VCs</h3>
                  </div>
                  <span className="hidden text-[9px] font-medium text-slate-400 sm:block">Sign in to apply</span>
                </div>

                {recommendationsLoading ? (
                  <div className="grid gap-3 sm:grid-cols-3">
                    {[0, 1, 2].map((i) => (
                      <div key={i} className="h-[164px] lg:h-[108px] animate-pulse rounded-2xl bg-slate-100" />
                    ))}
                  </div>
                ) : recommendedPrograms.length > 0 ? (
                  <div className="grid gap-3 sm:grid-cols-3">
                    {recommendedPrograms.map((rec) => {
                      const recIsIncubator = rec.entity_type === 'incubator';
                      const desc = stripHtml(rec.description || rec.fund_thesis || rec.hero_text || 'A live opportunity currently accepting applications.');
                      return (
                        <button
                          type="button"
                          key={rec.id || rec.slug}
                          onClick={() => {
                            if (isLoggedIn) {
                              navigate(`/apply/${rec.slug}`);
                              return;
                            }
                            navigate('/login', { state: { role: 'startup', mode: 'login', prefilledEmail: applicantEmail, returnTo: '/startup/programs' } });
                          }}
                          className="group relative min-h-[170px] lg:min-h-[108px] w-full overflow-hidden rounded-2xl border border-slate-200 bg-slate-950 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
                          data-testid={`recommended-program-${rec.slug}`}
                          aria-label={`Log in to apply to ${rec.name}`}
                        >
                          {rec.cover_image ? (
                            <img src={fileUrl(rec.cover_image)} alt="" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
                          ) : (
                            <div className={`absolute inset-0 bg-gradient-to-br ${recIsIncubator ? 'from-amber-500 via-orange-500 to-slate-950' : 'from-violet-600 via-indigo-600 to-slate-950'}`} />
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/55 to-black/15" />
                          <div className="relative z-10 flex min-h-[170px] lg:min-h-[108px] flex-col p-3.5 lg:p-2.5 text-white">
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex min-w-0 items-center gap-2">
                                {rec.fund_logo_url ? (
                                  <img src={fileUrl(rec.fund_logo_url)} alt="" className="h-7 w-7 shrink-0 rounded-md bg-white/10 object-cover" />
                                ) : (
                                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-white/15"><Buildings size={14} weight="fill" /></div>
                                )}
                                <span className="truncate text-[10px] font-semibold text-white/85">{rec.fund_name}</span>
                              </div>
                              <span className="shrink-0 rounded-full border border-white/20 bg-black/20 px-2 py-1 text-[7px] font-bold uppercase tracking-[0.12em] text-white/75 backdrop-blur-sm">
                                {recIsIncubator ? 'Incubator' : 'VC'}
                              </span>
                            </div>
                            <div className="mt-auto pt-6 lg:pt-1.5">
                              <h4 className="line-clamp-2 font-heading text-[13px] font-bold leading-[1.25] sm:text-[14px]">{rec.name}</h4>
                              <p className="mt-1 line-clamp-2 text-[9px] leading-[1.45] text-white/65 lg:hidden">{desc}</p>
                              <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[8px] font-medium text-white/60 lg:mt-1">
                                <span>{rec.questions_count || 0} application questions</span>
                                <span className="text-white/30">•</span>
                                {rec.open_until && <span>Closes {new Date(rec.open_until).toLocaleDateString([], { day: 'numeric', month: 'short' })}</span>}
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-[11px] leading-5 text-slate-500">
                    No other public programs are live right now. FounderBoard will show new opportunities here as they open.
                  </div>
                )}
              </div>

              {!isLoggedIn ? (
                <div className="mt-5 lg:mt-3" data-testid="login-programs-card">
                  <button
                    onClick={() => navigate('/login', { state: { role: 'startup', mode: 'login', prefilledEmail: applicantEmail, returnTo: '/startup/programs' } })}
                    className="group flex w-full items-center justify-center gap-2 rounded-xl bg-[#0d0b18] px-5 py-3.5 lg:py-2.5 text-sm font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-violet-700 hover:shadow-lg"
                    data-testid="login-cta"
                  >
                    Log in to FounderBoard
                    <ArrowRight size={17} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
                  </button>
                  <p className="mt-2 text-center text-[9px] leading-4 text-slate-400 sm:text-[10px] lg:hidden">
                    {accountCreated
                      ? <>Login details were sent to <strong className="break-all font-semibold text-slate-500">{applicantEmail}</strong>.</>
                      : 'Sign in to reuse your profile, apply to these programs and track your applications.'}
                  </p>
                </div>
              ) : (
                <button
                  onClick={() => navigate('/startup/programs')}
                  className="group mt-5 lg:mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-[#0d0b18] px-5 py-3.5 lg:py-2.5 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-violet-700"
                >
                  Explore all live programs
                  <ArrowRight size={16} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
                </button>
              )}

              {!isLoggedIn && (
                <button
                  onClick={() => navigate('/')}
                  className="mt-5 lg:mt-2 min-h-9 lg:min-h-0 text-[11px] font-medium text-slate-400 transition-colors hover:text-slate-700"
                  data-testid={accountCreated ? 'skip-login' : 'skip-signup'}
                >
                  Skip for now
                </button>
              )}
            </div>

            <div className="relative z-10 flex items-center justify-between border-t border-slate-100 pt-4 lg:pt-2 text-[9px] text-slate-300">
              <span>FounderBoard © 2026</span>
              <span>One workspace. One startup story.</span>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // ============= PROGRAM PAGE =============
  if (program.status === 'scheduled' || program.status === 'closed') return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-2xl mx-auto">

        {/* Investor branding header */}
        <div className={`${isIncubator ? 'bg-warning/5 border-warning/30' : 'bg-primary/5 border-primary/30'} border rounded-xl p-7 mb-5`} data-testid="apply-hero">
          <div className="flex items-center gap-4 mb-5">
            {program.fund_logo_url ? (
              <img src={fileUrl(program.fund_logo_url)} className="w-14 h-14 rounded-lg object-cover" alt={program.fund_name} />
            ) : (
              <div className={`w-14 h-14 rounded-lg ${isIncubator ? 'bg-warning/20' : 'bg-primary/15'} flex items-center justify-center`}>
                <Buildings size={28} className={isIncubator ? 'text-warning' : 'text-primary'} weight="bold" />
              </div>
            )}
            <div>
              <div className="text-xs uppercase tracking-wider text-text-muted">{isIncubator ? 'Incubator Program' : 'Funding Watchlist'}</div>
              <div className="font-heading font-bold text-lg text-text-primary">{program.fund_name}</div>
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-text-primary mb-2">{program.hero_text || `Apply to ${program.name}`}</h1>
          <h2 className="text-base text-text-secondary">{program.name}</h2>
          {program.description && <RichText html={program.description} className="text-sm text-text-secondary mt-3 leading-relaxed" />}
          {program.status === 'open' && program.open_until && (
            <div className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-medium text-warning bg-warning/10 border border-warning/30 rounded-lg px-3 py-1.5" data-testid="apply-deadline">
              <Clock size={13} weight="bold" /> Applications close on {new Date(program.open_until).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
            </div>
          )}
          {program.fund_website && (
            <a href={program.fund_website} target="_blank" rel="noreferrer" className={`inline-flex items-center gap-1.5 text-xs ${isIncubator ? 'text-warning' : 'text-primary'} hover:underline mt-3`}>
              <Globe size={12} /> {program.fund_website}
            </a>
          )}
        </div>

        {program.status === 'scheduled' ? (
          <div
            className="bg-surface border border-border rounded-xl p-8 text-center"
            data-testid="program-not-open"
          >
            <Clock size={36} className="text-text-muted mx-auto mb-3" />

            <h3 className="font-heading font-bold text-lg text-text-primary mb-1">
              Applications Are Not Open Yet
            </h3>

            <p className="text-sm text-text-secondary">
              Please visit again on{' '}
              <strong className="text-text-primary">
                {program.scheduled_open_at
                  ? new Date(program.scheduled_open_at).toLocaleString([], {
                      dateStyle: 'long',
                      timeStyle: 'short',
                    })
                  : 'to apply.'}
              </strong>{' '}
              to apply.
            </p>
          </div>
        ) : (
          <div
            className="bg-surface border border-border rounded-xl p-8 text-center"
            data-testid="program-closed"
          >
            <Lock size={36} className="text-text-muted mx-auto mb-3" />

            <h3 className="font-heading font-bold text-lg text-text-primary mb-1">
              Applications closed
            </h3>

            <p className="text-sm text-text-secondary">
              {program.fund_name} is no longer accepting applications.
            </p>
          </div>
        )}
      </div>
    </div>
  );

  // ============= OPEN — split carousel/form layout =============
  return (
    <>
      <ProgramPublicFrame program={program} isIncubator={isIncubator}
        topRightAction={showApplicationBackButton ? (
          <button
            type="button"
            onClick={handleApplicationBack}
            className="inline-flex items-center gap-1 text-xs text-text-muted transition-colors hover:text-text-primary focus:outline-none focus-visible:underline"
            data-testid="application-go-back"
            aria-label={applicationBackLabel}
          >
            <CaretLeft size={11} weight="bold" />
            <span>{applicationBackLabel}</span>
          </button>
        ) : null}
      >
        {isLoggedIn && myStartup ? (
          // ====== AUTHED FOUNDERBOARD APPLY (option 4b) ======
          <div data-testid="authed-apply">
            <SectionedForm
              questions={program.questions || []}
              answers={answers}
              setAnswers={setAnswers}
              fileUploads={fileUploads}
              uploadingQid={uploadingQid}
              onUpload={(qid, file) => uploadQuestionFile(qid, file, true)}
              onSubmit={submitAuthed}
              submitting={submitting}
              submitLabel="Submit"
              submitTestId="authed-submit-btn"
              resumeKey={draftRestored ? 'authed-draft' : ''}
              accentBtn={isIncubator ? 'bg-warning hover:bg-warning/90' : 'bg-primary hover:bg-primary/90'}
              footerExtra={
                <button type="button" onClick={saveDraft} disabled={savingDraft}
                  className="px-4 py-2.5 text-sm font-medium border border-border rounded-lg text-text-secondary hover:text-text-primary inline-flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="save-draft-btn">
                  <FloppyDisk size={13} weight="bold" /> {savingDraft ? 'Saving…' : 'Save as draft'}
                </button>
              }
              leadingFields={
                <>
                  <div className="flex items-center gap-2 mb-4 text-success text-xs font-medium uppercase tracking-wider"><CheckCircle size={14} weight="fill" /> You're logged in to Founderboard</div>
                  <div className="bg-background border border-border rounded-lg p-4 mb-5 flex items-center gap-3" data-testid="my-profile-preview">
                    {myStartup.logo_url ? (
                      <img src={fileUrl(myStartup.logo_url)} className="w-12 h-12 rounded-lg object-cover" alt="" />
                    ) : (
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                        <span className="text-primary font-mono font-bold">{myStartup.startup_name?.substring(0, 2).toUpperCase()}</span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-wider text-text-muted">Applying as</div>
                      <div className="font-heading font-bold text-text-primary truncate">{myStartup.startup_name}</div>
                      <div className="text-[12px] text-text-secondary truncate">{myStartup.sector} · {myStartup.funding_stage} · {myStartup.hq_city}</div>
                    </div>
                  </div>
                  <p className="text-sm text-text-secondary mb-4">{program.fund_name} will see your full Founderboard profile, monthly updates, metrics & founder details. Just answer their questions:</p>

                  {draftRestored && (
                    <div className="mb-4 flex items-center gap-2 text-[12px] text-info bg-info/10 border border-info/30 rounded-lg px-3 py-2" data-testid="draft-restored-banner">
                      <FloppyDisk size={13} weight="fill" className="flex-shrink-0" />
                      <span>Your saved draft is loaded. Finish it now or save again to keep progressing later.</span>
                    </div>
                  )}

                  <div className="mb-4">
                    <label className="block text-[11px] uppercase tracking-wider text-text-secondary mb-1.5">Phone <span className="text-danger font-bold">*</span></label>
                    <PhoneField
                      value={applicantPhone}
                      onChange={setApplicantPhone}
                      placeholder="98xxx xxxxx"
                      compact
                      data-testid="applicant-phone-input-authed"
                    />
                  </div>
                </>
              }
            />
          </div>
        ) : (
          // ====== UNAUTH PATH — apply choice lives in a popup, not inline ======
          <>
            {!pickedQuick ? (
              /* In-page prompt — the actual choice is made in the popup */
              <ProgramApplyPrompt program={program} isIncubator={isIncubator} onApply={() => setShowChoiceModal(true)} />
            ) : (
            /* Quick Apply form (shown after picking "Quick Apply" in the popup) */
            <form onSubmit={submitAnonymous} className="space-y-5" id="quick-apply-form" data-testid="apply-form">
              <SectionedForm
                questions={program.questions || []}
                answers={answers}
                setAnswers={setAnswers}
                fileUploads={fileUploads}
                uploadingQid={uploadingQid}
                onUpload={(qid, file) => uploadQuestionFile(qid, file, false)}
                onSubmit={submitAnonymous}
                submitting={submitting}
                submitLabel="Submit Quick Apply"
                submitTestId="submit-application-btn"
                resumeKey={quickDraftResumed ? quickDraftToken : ''}
                accentBtn={isIncubator ? 'bg-warning hover:bg-warning/90' : 'bg-primary hover:bg-primary/90'}
                footerExtra={
                  <button type="button" onClick={saveQuickDraft} disabled={savingDraft}
                    className="px-4 py-2.5 text-sm font-medium border border-border rounded-lg text-text-secondary hover:text-text-primary inline-flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="quick-save-draft-btn">
                    <FloppyDisk size={13} weight="bold" /> {savingDraft ? 'Saving…' : 'Save as draft'}
                  </button>
                }
                footerHint={quickDraftToken
                  ? 'Draft saved — resume from your emailed link, no login needed.'
                  : 'No account needed — we’ll email you a link to finish later.'}
                leadingFields={
                  <>
                    {quickDraftToken && (
                      <div className="flex items-start gap-2 text-[12px] text-info bg-info/10 border border-info/30 rounded-lg px-3 py-2" data-testid="quick-draft-restored-banner">
                        <FloppyDisk size={13} weight="fill" className="flex-shrink-0 mt-0.5" />
                        <span>
                          {quickDraftResumed ? 'Draft loaded — answers, choices and uploads are all back. ' : 'Draft saved. '}
                          {quickDraftSavedAt ? `Last saved ${new Date(quickDraftSavedAt).toLocaleString()}. ` : ''}
                          We keep it for 30 days — resume from the link in your inbox, no login needed.
                        </span>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[11px] uppercase tracking-wider text-text-secondary mb-1.5">Your name <span className="text-danger font-bold">*</span></label>
                        <input type="text" value={applicantName} onChange={e => setApplicantName(e.target.value)} placeholder="Jane Founder" required
                          className="w-full bg-white border border-border rounded-lg px-3 py-2.5 text-base md:text-sm text-text-primary focus:outline-none focus:border-primary" data-testid="applicant-name-input" />
                      </div>
                      <div>
                        <label className="block text-[11px] uppercase tracking-wider text-text-secondary mb-1.5">Email <span className="text-danger font-bold">*</span></label>
                        <input type="email" value={applicantEmail} onChange={e => setApplicantEmail(e.target.value)} placeholder="you@startup.com" required
                          className="w-full bg-white border border-border rounded-lg px-3 py-2.5 text-base md:text-sm text-text-primary focus:outline-none focus:border-primary" data-testid="applicant-email-input" />
                        {emailCheck?.exists && emailCheck.role === 'startup' && (
                          <button type="button"
                            onClick={() => { localStorage.setItem('pending_program_apply_slug', slug); navigate('/login', { state: { role: 'startup', mode: 'login', prefilledEmail: applicantEmail, returnTo: `/startup/programs?apply=${slug}` } }); }}
                            className="mt-2 w-full text-left bg-info/10 border border-info/30 rounded-lg px-3 py-2 text-[12px] text-info hover:bg-info/15 flex items-center justify-between gap-2"
                            data-testid="email-already-exists-prompt">
                            <span>This email already has a Founderboard profile. <strong>Log in to apply</strong> with your full data.</span>
                            <ArrowRight size={12} weight="bold" className="flex-shrink-0" />
                          </button>
                        )}
                        {/* Typed an email that has an unfinished draft → offer the way back in. */}
                        {draftStatus?.exists && !quickDraftToken && (
                          <div className="mt-2 bg-warning/10 border border-warning/30 rounded-lg px-3 py-2 text-[12px] text-warning" data-testid="quick-draft-found-prompt">
                            <div className="flex items-start gap-1.5">
                              <Clock size={13} weight="bold" className="flex-shrink-0 mt-0.5" />
                              <span>
                                You have an unfinished application for this program
                                {draftStatus.updated_at ? ` from ${new Date(draftStatus.updated_at).toLocaleDateString()}` : ''}
                                {draftStatus.answered_count ? ` with ${draftStatus.answered_count} answer${draftStatus.answered_count === 1 ? '' : 's'} filled in` : ''}.
                              </span>
                            </div>
                            <button type="button" onClick={sendResumeLink} disabled={sendingResumeLink}
                              className="mt-1.5 font-semibold underline hover:no-underline disabled:opacity-50"
                              data-testid="send-resume-link-btn">
                              {sendingResumeLink ? 'Sending…' : 'Email me the link to resume it →'}
                            </button>
                          </div>
                        )}
                        {draftStatus?.already_applied && (
                          <div className="mt-2 bg-surface border border-border rounded-lg px-3 py-2 text-[12px] text-text-secondary" data-testid="quick-already-applied-prompt">
                            This email has already applied to this program.
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-[11px] uppercase tracking-wider text-text-secondary mb-1.5">Phone <span className="text-danger font-bold">*</span></label>
                      <PhoneField
                        value={applicantPhone}
                        onChange={setApplicantPhone}
                        placeholder="98xxx xxxxx"
                        compact
                        data-testid="applicant-phone-input"
                      />
                    </div>
                  </>
                }
              />
            </form>
            )}

            {/* Apply-choice popup — mobile follows the dedicated 390px conversion layout;
                desktop/tablet keeps the original split modal unchanged. */}
            {showChoiceModal && (
              <div
                className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-slate-950/35 backdrop-blur-[2px] md:items-center md:p-8"
                data-testid="apply-choice-modal"
                onMouseDown={(e) => { if (e.target === e.currentTarget) setShowChoiceModal(false); }}
              >
                {/* Mobile-only conversion card */}
                <div className="w-screen max-w-none rounded-t-[24px] border-x-0 border-b-0 border-t border-slate-200 bg-[#F7F7F7] md:hidden">
                  <div className="flex flex-col gap-4 p-4">
                    <div className="relative flex h-[45px] w-full items-start justify-between">
                      <div className="inline-flex h-10 items-center gap-3">
                        <img
                          src="/founderboard-logo.png"
                          alt="FounderBoard"
                          className="h-9 w-9 rounded-lg object-contain"
                        />
                        <span className="font-heading text-[18px] font-extrabold tracking-[-0.3px] text-[#111118]">FounderBoard</span>
                      </div>
                    </div>

                    <section
                      className="flex w-full flex-col gap-[30px] rounded-2xl border border-teal-200/80 bg-white p-[18px] shadow-[0_2px_8px_rgba(0,0,0,0.02)]"
                      data-testid="founderboard-option-mobile"
                    >
                      <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-1.5">
                          <span className="rounded border border-teal-600 bg-white px-2.5 py-1 font-mono text-[10px] font-bold uppercase leading-[13px] tracking-[1px] text-teal-700">
                            Recommended path
                          </span>
                          <span className="rounded border border-teal-200 bg-teal-100 px-2.5 py-1 font-mono text-[10px] font-bold uppercase leading-[13px] tracking-[1px] text-teal-700">
                            Free
                          </span>
                        </div>
                        <h2 className="font-sans text-[26px] font-extrabold leading-[1.2] text-slate-900">
                          Your Ultimate Advantage is here.
                        </h2>
                      </div>

                      <ul className="flex flex-col gap-5 text-[14px] font-medium leading-[18px] text-[#232322]">
                        <li className="flex items-center gap-3"><ChartLineUp size={20} weight="bold" className="shrink-0 text-teal-700" /> Investor sees live metrics & runway</li>
                        <li className="flex items-center gap-3"><FileText size={20} weight="bold" className="shrink-0 text-teal-700" /> Track all your applications</li>
                        <li className="flex items-center gap-3"><Lightning size={20} weight="fill" className="shrink-0 text-teal-700" /> Get matched to more programs</li>
                        <li className="flex items-center gap-3 font-bold text-teal-700"><ShieldCheck size={20} weight="fill" className="shrink-0" /> 3x higher acceptance rate</li>
                      </ul>

                      <div className="flex flex-col gap-3">
                        <button
                          onClick={() => {
                            localStorage.setItem('pending_program_apply_slug', slug);
                            navigate('/login', { state: { role: 'startup', mode: 'signup', returnTo: `/startup/programs?apply=${slug}` } });
                          }}
                          className="flex h-12 w-full items-center justify-center rounded-xl bg-[#007C71] px-6 text-[15px] font-extrabold leading-5 text-white shadow-[0_4px_16px_rgba(13,148,136,0.16)] transition hover:bg-teal-800"
                          data-testid="cta-with-founderboard-mobile"
                        >
                          Apply with Founderboard Profile
                        </button>
                        <div className="flex h-4 items-center justify-center gap-2 text-[12px] font-semibold leading-4 text-slate-600">
                          <div className="flex w-10 items-center">
                            <span className="h-4 w-4 rounded-full border border-white bg-slate-300" />
                            <span className="-ml-1 h-4 w-4 rounded-full border border-white bg-slate-400" />
                            <span className="-ml-1 h-4 w-4 rounded-full border border-white bg-slate-500" />
                          </div>
                          <span>195 founders chose this path</span>
                        </div>
                      </div>

                      <div className="flex h-[15px] items-center gap-3">
                        <div className="h-px flex-1 bg-slate-300" />
                        <span className="text-[12px] font-medium uppercase leading-[15px] tracking-[0.08em] text-slate-300">or</span>
                        <div className="h-px flex-1 bg-slate-300" />
                      </div>

                      <div className="flex items-center justify-between pt-2">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-[14px] font-bold leading-[18px] text-slate-900">Basic Apply</span>
                          <span className="text-[11px] leading-[14px] text-slate-500">Manual form entry, no profile sync</span>
                        </div>
                        <button
                          onClick={() => { setPickedQuick(true); setShowChoiceModal(false); }}
                          className="rounded-lg border-[1.5px] border-slate-200 bg-white px-3 py-2 text-[13px] font-semibold leading-[17px] text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900"
                          data-testid="cta-quick-apply-mobile"
                        >
                          Basic Form
                        </button>
                      </div>
                    </section>
                  </div>
                </div>

                {/* Original regular-view modal — intentionally unchanged */}
                <div className="relative hidden w-full max-w-[920px] my-4 md:my-0 overflow-hidden rounded-[22px] border border-white/70 bg-white shadow-[0_28px_90px_rgba(15,23,42,0.28)] md:block">

                  <div className="grid min-h-[500px] grid-cols-1 md:grid-cols-[1fr_1.72fr]">
                    {/* Basic application path */}
                    <section className="flex min-h-[390px] flex-col border-t border-slate-200 bg-white px-8 pb-8 pt-9 md:min-h-[500px] md:border-r md:border-t-0 md:px-9 md:pt-10" data-testid="quick-apply-option">
                      <div>
                        <span className="inline-flex rounded bg-slate-100 px-2 py-1 text-[9px] font-bold uppercase tracking-[0.18em] text-slate-500">
                          Alternative
                        </span>
                        <h3 className="mt-5 font-heading text-[25px] font-bold tracking-[-0.025em] text-slate-900">Basic Apply</h3>
                        <p className="mt-2 text-[12px] leading-5 text-slate-500">A simple application without setting up a Founderboard profile.</p>
                      </div>

                      <ul className="mt-auto space-y-2 pb-8 text-[11px] leading-[1.45] text-slate-500">
                        <li className="flex gap-2"><span>•</span><span>Just the basics. No profile setup or sync required.</span></li>
                        <li className="flex gap-2"><span>•</span><span>Requires manually re-entering fundraising details.</span></li>
                        <li className="flex gap-2"><span>•</span><span>No application analytics or program matching updates.</span></li>
                      </ul>

                      <button
                        onClick={() => { setPickedQuick(true); setShowChoiceModal(false); }}
                        className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3.5 text-[12px] font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900"
                        data-testid="cta-quick-apply"
                      >
                        Continue with Basic Form
                      </button>
                    </section>
                    {/* Recommended Founderboard path */}
                    <section
                      className="relative isolate flex flex-col overflow-hidden px-5 pb-6 pt-6 sm:px-8 sm:pb-7 sm:pt-8 md:min-h-[500px] md:px-11 md:pb-8 md:pt-10"
                      data-testid="founderboard-option"
                    >
                      {/* Soft futuristic backdrop inspired by the supplied reference. */}
                      <div className="pointer-events-none absolute inset-0 -z-20 bg-[linear-gradient(135deg,#fbffff_0%,#f4faf9_48%,#eef5f6_100%)]" />
                      <div className="pointer-events-none absolute -left-20 -top-24 -z-10 h-48 w-48 rounded-full bg-emerald-100/65 blur-3xl sm:h-72 sm:w-72" />
                      <div className="pointer-events-none absolute -bottom-24 right-6 -z-10 h-48 w-48 rounded-full bg-cyan-100/60 blur-3xl sm:h-72 sm:w-72" />
                      <div className="pointer-events-none absolute inset-x-0 bottom-0 -z-10 h-[58%] opacity-45"
                        style={{
                          backgroundImage: 'linear-gradient(115deg, transparent 0 25%, rgba(15,118,110,.10) 25.3% 25.8%, transparent 26.1% 42%, rgba(15,118,110,.08) 42.3% 42.8%, transparent 43.1%), linear-gradient(25deg, transparent 0 36%, rgba(15,118,110,.08) 36.3% 36.8%, transparent 37.1%)',
                          backgroundSize: '180px 130px, 220px 150px'
                        }}
                      />

                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded border border-teal-500/45 bg-white/75 px-2 py-1 text-[9px] font-bold uppercase tracking-[0.18em] text-teal-700">
                          Recommended path
                        </span>
                        <span className="rounded bg-emerald-100 px-2 py-1 text-[9px] font-bold uppercase tracking-[0.18em] text-emerald-700">
                          Free
                        </span>
                      </div>

                      <div className="mt-3 sm:mt-5 max-w-[430px]">
                        <h2 className="font-heading text-[24px] sm:text-[32px] font-bold leading-[1.15] tracking-[-0.035em] text-slate-900 md:text-[38px]">
                          Your Ultimate<br className="hidden sm:block" /> Advantage is here.
                        </h2>
                        <p className="mt-2 sm:mt-3 text-[12px] leading-5 text-slate-500">
                          Apply using your Founderboard profile and carry your startup story, metrics and progress with you.
                        </p>
                      </div>

                      <div className="mt-4 sm:mt-auto sm:pt-9">
                        {/* Full benefit list is a sm+ nicety — on a phone, the two CTAs
                            are what matters and this is what was pushing the popup
                            taller than the screen. */}
                        <ul className="hidden sm:block space-y-2 sm:space-y-3 text-[12px] font-medium text-slate-700">
                          <li className="flex items-center gap-2.5"><ChartLineUp size={16} weight="bold" className="shrink-0 text-teal-600" /> Investor sees live metrics & runway</li>
                          <li className="flex items-center gap-2.5"><FileText size={16} weight="bold" className="shrink-0 text-teal-600" /> Track all your applications</li>
                          <li className="flex items-center gap-2.5"><Lightning size={16} weight="fill" className="shrink-0 text-teal-600" /> Get matched to more programs</li>
                          <li className="flex items-center gap-2.5 font-bold text-teal-700"><ShieldCheck size={16} weight="fill" className="shrink-0" /> 3x higher acceptance rate</li>
                        </ul>

                        <button
                          onClick={() => {
                            localStorage.setItem('pending_program_apply_slug', slug);
                            navigate('/login', { state: { role: 'startup', mode: 'signup', returnTo: `/startup/programs?apply=${slug}` } });
                          }}
                          className="mt-3 sm:mt-8 flex w-full items-center justify-center rounded-lg bg-teal-700 px-4 py-3 sm:py-3.5 text-[12px] font-bold text-white shadow-[0_8px_22px_rgba(15,118,110,0.22)] transition hover:bg-teal-800 hover:shadow-[0_10px_28px_rgba(15,118,110,0.30)]"
                          data-testid="cta-with-founderboard"
                        >
                          Apply with Founderboard Profile
                        </button>
                      </div>
                    </section>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </ProgramPublicFrame>
    </>
  );
};

export default PublicApply;
